// src/components/admin/UserSidebar.js
import React from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const drawerWidth = 240;
const collapsedWidth = 70;

const UserSidebar = ({ isOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    navigate("/");
  };

  const menuItems = [
    {
      text: "الرئيسية",
      icon: <DashboardIcon />,
      path: "/admin-dashboard",
    },
    {
      text: "نظامي",
      icon: <AccountBoxIcon />,
      path: "/user-profile",
    },
    {
      text: "الباقات",
      icon: <WorkspacePremiumIcon />,
      path: "/user-Packages",
    },
    {
      text: "الدعم الفني",
      icon: <SupportAgentIcon />,
      path: "/user-mytickets",
    },
  ];

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      sx={{
        width: isOpen ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isOpen ? drawerWidth : collapsedWidth,
          boxSizing: "border-box",
          overflowX: "hidden",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%)",
          color: "#fff",
          boxShadow: "-4px 0px 20px rgba(0, 0, 0, 0.15)",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.05) 25%, transparent 25%)",
            backgroundSize: "20px 20px",
            opacity: 0.3,
            pointerEvents: "none"
          }
        },
      }}
      open
    >
      <Toolbar
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: isOpen ? "space-between" : "center",
          alignItems: "center",
          px: isOpen ? 3 : 1.5,
          py: 2,
          borderBottom: "2px solid rgba(255,255,255,0.15)",
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(10px)"
        }}
      >
        {isOpen && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "700",
              color: "white",
              fontSize: "1.2rem",
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              background: "linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            لوحة التحكم
          </Typography>
        )}

        <motion.div 
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.1 }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)",
              color: "white",
              width: 45,
              height: 45,
              border: "1px solid rgba(255,255,255,0.2)",
              position: "relative",
              right: "-28px", // Move toggle button to the right
              "&:hover": {
                background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)",
                transform: "rotate(180deg)",
                borderColor: "rgba(255,255,255,0.4)"
              },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
            }}
          >
            {isOpen ? <MenuOpenIcon fontSize="medium" /> : <MenuIcon fontSize="medium" />}
          </IconButton>
        </motion.div>
      </Toolbar>

      <List sx={{ 
        flexGrow: 1, 
        width: "100%", 
        px: isOpen ? 2 : 1, 
        py: 2,
        position: "relative",
        right: "-10px" // Move all list items to the right
      }}>
        {menuItems.map((item, index) => (
          <motion.div
            key={item.text}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              delay: index * 0.1,
              duration: 0.5,
              ease: "easeOut"
            }}
          >
            <Tooltip
              title={!isOpen ? item.text : ""}
              placement="left"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(0,0,0,0.8)',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }
                }
              }}
            >
              <ListItem
                button
                onClick={() => navigate(item.path)}
                sx={{
                  width: "100%",
                  borderRadius: "16px",
                  mb: 1,
                  py: 1.5,
                  px: isOpen ? 2.5 : 1.5,
                  color: "white",
                  justifyContent: isOpen ? "flex-start" : "center",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  position: "relative",
                  right: "-5px", // Additional right movement for individual items
                  "&:hover": {
                    background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.1) 100%)",
                    transform: "translateX(-8px) scale(1.02)",
                    borderColor: "rgba(255,255,255,0.3)",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.25)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "#60a5fa",
                    minWidth: 0,
                    mr: isOpen ? 3 : 0,
                    justifyContent: "center",
                    "& svg": {
                      fontSize: "1.5rem",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                {isOpen && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      "& span": {
                        fontWeight: "600",
                        fontSize: "1rem",
                        letterSpacing: "0.5px",
                        textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                      },
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          </motion.div>
        ))}
      </List>

      <Box sx={{ 
        borderTop: "2px solid rgba(255,255,255,0.15)", 
        pt: 2,
        background: "rgba(255,255,255,0.05)",
        backdropFilter: "blur(10px)",
        position: "relative",
        right: "-10px" // Move logout section to the right as well
      }}>
        <Tooltip 
          title={!isOpen ? "تسجيل الخروج" : ""} 
          placement="left" 
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(244, 67, 54, 0.9)',
                fontSize: '0.9rem',
                fontWeight: 500
              }
            }
          }}
        >
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              mx: isOpen ? 2 : 1,
              mb: 2,
              borderRadius: "16px",
              py: 1.5,
              px: isOpen ? 2.5 : 1.5,
              justifyContent: isOpen ? "flex-start" : "center",
              color: "#fca5a5",
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.15) 100%)",
                transform: "translateX(-8px) scale(1.02)",
                borderColor: "rgba(239, 68, 68, 0.5)",
                boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <ListItemIcon
              sx={{
                color: "#fca5a5",
                minWidth: 0,
                mr: isOpen ? 3 : 0,
                justifyContent: "center",
                "& svg": {
                  fontSize: "1.5rem",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary="تسجيل الخروج"
                sx={{
                  "& span": {
                    fontWeight: "600",
                    fontSize: "1rem",
                    letterSpacing: "0.5px",
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                  },
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default UserSidebar;
